'use client';
import { useState, useEffect, FC } from 'react';
import { Category, Variant } from 'shared/types/product';
import Head from 'next/head';
import { Product } from 'shared/types/product/Product';
import { UIProduct } from 'components/commercetools-ui/organisms/product/product-details/types';
import usePath from 'helpers/hooks/usePath';
import usePreloadImages from 'helpers/hooks/usePreloadImages';
import { toUIColor } from 'helpers/mappers/toUIColor';
import { toUIProduct } from 'helpers/mappers/toUIProduct';
import { toUISize } from 'helpers/mappers/toUIsize';
import ProductDetails, { ProductDetailsProps } from '..';
import { VARAINT_CHANGE_EVENT } from 'helpers/constants/events';
import { COLOR } from 'helpers/constants/attributes';
import { useParams } from 'next/navigation';
import { getPdpUrlWithLocale, getSkuFromUrlOrSlug } from 'helpers/utils/pdpUrlHelper';

type ProductDetailsAdapterProps = {
  product: Product;
  inModalVersion?: ProductDetailsProps['inModalVersion'];
  setIsOpen?: (value: boolean) => void;
  onAddToCart?: () => void;
  // Server-seeded props (SSR). Absent in the modal / quick-view usage.
  initialSku?: string;
  initialBundleData?: any[];
  initialSparePartsData?: any[];
  categories?: Category[];
};

// Resolve the variant to render first. Server and client agree on this because it
// is derived only from props (initialSku from the URL slug) — no browser APIs.
const resolveInitialVariant = (product: Product, initialSku?: string, inModalVersion?: boolean): Variant => {
  if (inModalVersion) return product?.variants?.[0];
  const found = initialSku ? product?.variants?.find(({ sku }) => sku == initialSku) : undefined;
  return found ?? product?.variants?.[0];
};

// Pure mapping to the UI product shape (same logic the effect used previously).
const mapUIProduct = (product: Product, variant: Variant): UIProduct => {
  const colors = toUIColor(product);
  const sizes = toUISize(product);
  const productToUse = toUIProduct(product, variant, colors, sizes);
  return { ...productToUse, images: [productToUse.images[0]] };
};

const ProductDetailsAdapter: FC<ProductDetailsAdapterProps> = ({
  product,
  inModalVersion,
  setIsOpen,
  onAddToCart,
  initialSku,
  initialBundleData = [],
  initialSparePartsData = [],
  categories = [],
}) => {
  const { path } = usePath();

  // Seed synchronously so the first (server) render already has the product content
  // in the SSR HTML instead of returning an empty fragment until an effect runs.
  const [variant, setVariant] = useState<Variant | undefined>(() =>
    resolveInitialVariant(product, initialSku, inModalVersion),
  );
  const [mappedProduct, setMappedProduct] = useState<UIProduct | undefined>(() => {
    const initialVariant = resolveInitialVariant(product, initialSku, inModalVersion);
    return initialVariant ? mapUIProduct(product, initialVariant) : undefined;
  });

  usePreloadImages(product.variants.map((variant) => variant.images ?? []).flat());

  useEffect(() => {
    if (product && variant) {
      setMappedProduct(mapUIProduct(product, variant));
    }
  }, [product, variant]);

  useEffect(() => {
    if (!product) return;

    if (inModalVersion) {
      setVariant(product?.variants[0]);
    } else {
      const currentVariantSKU = getSkuFromUrlOrSlug(path);
      const currentVariantIndex = product?.variants.findIndex(({ sku }) => sku == currentVariantSKU);
      setVariant(product.variants[currentVariantIndex] ?? product.variants?.[0]);
    }
  }, [inModalVersion, product, path]);
  const { locale } = useParams();
  const handleChangeVariant = (sku: string) => {
    const variantsToUse = product.variants.find((variant) => variant.sku === sku);
    setVariant(variantsToUse);
    if(product.slug){
      const newUrl = getPdpUrlWithLocale(locale, product.slug, variantsToUse?.sku, variantsToUse?.attributes?.colour);
      window.history.pushState({}, '', newUrl);
    }
  };


  if (!product || !variant) return <></>;

  return (
    <>
    <ProductDetails
      product={mappedProduct as UIProduct}
      variant={variant}
      url={product._url}
      inModalVersion={inModalVersion}
      onChangeVariant={handleChangeVariant}
      setIsOpen={setIsOpen}
      onAddToCart={onAddToCart}
      initialBundleData={initialBundleData}
      initialSparePartsData={initialSparePartsData}
      categories={categories}
    />
    </>
  );
};

export default ProductDetailsAdapter;
